import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, User, MapPin, Phone, Mail, Wifi, MessageSquare, AlertTriangle, CheckCircle2, Send, Eye, EyeOff, Copy } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

// Partner-side ticket detail — read-only mirror of the admin
// AdminTicketDetail. Shows customer info, plan comparison, NBN login
// credentials (so partners can help their customers get connected), and
// the activity timeline. No canned responses (admin-only), no field
// edits (partners can't rewrite customer data through this surface —
// they still get the note-add + stage move on the Kanban if needed).

export default function PartnerTicketDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [activity, setActivity] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const [{ data: c }, { data: act }] = await Promise.all([
        api.get(`/partner/tickets/${id}`),
        api.get(`/partner/migration/clients/${id}/activity`),
      ]);
      setClient(c);
      setActivity(act.activity || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load ticket");
    }
  }
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [id]);

  async function addNote() {
    if (!noteBody.trim()) return;
    setBusy(true);
    try {
      await api.post(`/partner/migration/clients/${id}/notes`, { body: noteBody });
      setNoteBody("");
      toast.success("Note posted — Steele Cloud will see it in seconds");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Note failed");
    } finally { setBusy(false); }
  }

  if (!client) return <div className="p-10 text-slate-500">Loading ticket…</div>;
  const mismatch = client.plan_mismatch || {};
  const snap = client.matched_plan_snapshot || {};

  return (
    <div className="space-y-5 pb-16" data-testid={`partner-ticket-detail-${client.id}`}>
      <div className="flex items-center gap-4 flex-wrap">
        <Link to="/portal/tickets" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300"><ArrowLeft className="w-3 h-3" /> All tickets</Link>
        <span className="text-slate-600">·</span>
        <Link to="/portal/onboarding" className="text-xs text-slate-400 hover:text-cyan-300">Kanban view</Link>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold">{(client.stage || "").replace(/_/g, " ")}</div>
          <h1 className="font-display text-3xl tracking-tight mt-1">{client.business_name || client.contact_name || client.email}</h1>
          {client.linked_signup_id && <div className="text-xs text-emerald-300 mt-1 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Signed the online transfer form</div>}
        </div>
        {client.syncro_ticket_number && (
          <div className="text-sm text-slate-400 border border-slate-800 rounded-lg px-3 py-2">Ticket #{client.syncro_ticket_number}</div>
        )}
      </div>

      {mismatch.is_mismatch && !mismatch.acknowledged && (
        <div className="rounded-lg border-2 border-amber-500/60 bg-amber-500/10 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-amber-200 mb-1">Plan mismatch — Steele Cloud is reviewing</div>
            <ul className="text-xs text-amber-200/90 space-y-1 list-disc pl-5">{(mismatch.reasons || []).map((r, i) => <li key={`${i}-${r}`}>{r}</li>)}</ul>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Customer</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Field label="Business" icon={<User className="w-3.5 h-3.5" />} value={client.business_name} />
              <Field label="Contact" icon={<User className="w-3.5 h-3.5" />} value={client.contact_name} />
              <Field label="Email" icon={<Mail className="w-3.5 h-3.5" />} value={client.email} />
              <Field label="Phone" icon={<Phone className="w-3.5 h-3.5" />} value={client.phone} />
              <Field label="Address" icon={<MapPin className="w-3.5 h-3.5" />} value={client.service_address} span={2} />
              <Field label="ABN" value={client.abn} />
            </div>
          </section>

          <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4 inline-flex items-center gap-2"><Wifi className="w-3.5 h-3.5" /> Plan &amp; NBN credentials</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Field label="Current plan" value={client.current_plan_name} />
              <Field label="Current fee" value={client.current_monthly_fee ? `$${Number(client.current_monthly_fee).toFixed(2)}` : ""} />
              <Field label="Chosen plan" value={snap.name || client.chosen_plan} />
              <Field label="Chosen fee" value={snap.monthly_price ? `$${Number(snap.monthly_price).toFixed(2)}` : ""} />
              <Field label="NBN username" value={client.nbn_username} placeholder={!client.nbn_username ? "Awaiting NBN provider handover" : ""} mono />
              <Field label="NBN password" value={client.nbn_password} placeholder={!client.nbn_password ? "Awaiting NBN provider handover" : ""} mono secret />
              <Field label="NBN speed" value={client.nbn_speed} placeholder={!client.nbn_speed ? "Awaiting NBN provider handover" : ""} />
              <Field label="WSM service ID" value={client.nbn_wsm_service_id} placeholder={!client.nbn_wsm_service_id ? "Awaiting NBN provider handover" : ""} mono />
              <Field label="UNI-D port" value={client.nbn_unid_port} placeholder={!client.nbn_unid_port ? "Awaiting NBN provider handover" : ""} />
            </div>
            {(client.nbn_username || client.nbn_password) && (
              <div className="mt-3 text-[11px] text-amber-300 inline-flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Sensitive — do not share these credentials over unsecured channels.</div>
            )}
          </section>

          <section className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="partner-note-composer">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3 inline-flex items-center gap-2"><MessageSquare className="w-3.5 h-3.5" /> Add a note</h2>
            <p className="text-xs text-slate-500 mb-3">
              Notes are visible to the Steele Cloud team and mirrored to the Syncro ticket. Use this for questions about the customer, updates on their status, or anything Steele Cloud needs to know.
            </p>
            <textarea
              rows={3} value={noteBody} onChange={(e) => setNoteBody(e.target.value)}
              placeholder="e.g. Customer called — they want to change the porting date to next Wednesday."
              className="w-full bg-slate-950 border border-slate-800 focus:border-cyan-500 outline-none rounded-md px-3 py-2 text-sm"
              data-testid="partner-note-body"
            />
            <div className="mt-2 flex justify-end">
              <button
                onClick={addNote} disabled={busy || !noteBody.trim()}
                data-testid="partner-note-send"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-medium disabled:opacity-60"
              >
                <Send className="w-4 h-4" /> {busy ? "Posting…" : "Post note"}
              </button>
            </div>
          </section>
        </div>

        <div>
          <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3 inline-flex items-center gap-2"><MessageSquare className="w-3.5 h-3.5" /> Activity</h2>
            {activity.length === 0 ? (
              <div className="text-sm text-slate-500">No activity yet.</div>
            ) : (
              <ul className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                {activity.map(a => (
                  <li key={a.id} className="text-xs border-l-2 border-slate-800 pl-3 pb-3">
                    <div className="text-slate-500 mb-0.5"><span className="text-slate-300">{a.actor_email}</span> · <span>{new Date(a.created_at).toLocaleString(undefined, {month:"short", day:"2-digit", hour:"2-digit", minute:"2-digit"})}</span></div>
                    <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold">{a.action.replace(/_/g, " ")}</div>
                    {a.details?.body && <div className="text-slate-200 whitespace-pre-wrap mt-1">{a.details.body}</div>}
                    {a.details?.from && <div className="text-slate-400 mt-1">{a.details.from} → <span className="text-cyan-300">{a.details.to}</span></div>}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, icon, span = 1, placeholder = "", mono = false, secret = false }) {
  const [revealed, setRevealed] = useState(false);
  const isSecretHidden = secret && !revealed && !!value;
  const displayValue = isSecretHidden ? "•".repeat(Math.min(20, String(value).length)) : (value || placeholder || "—");
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold inline-flex items-center gap-1.5">{icon}{label}</div>
      <div className="mt-1 flex gap-1.5">
        <div className={`flex-1 bg-slate-950 border border-slate-800 rounded-md px-3 py-1.5 text-sm ${mono ? "font-mono" : ""} ${!value ? "text-slate-500 italic" : "text-slate-100"}`}>
          {displayValue}
        </div>
        {secret && value && (
          <button
            type="button"
            onClick={() => setRevealed((r) => !r)}
            className="px-2 rounded-md border border-slate-800 bg-slate-950 hover:bg-slate-800 text-slate-400 hover:text-slate-100"
            title={revealed ? "Hide password" : "Show password"}
            data-testid={`field-secret-toggle-${label.toLowerCase().replace(/\s+/g,"-")}`}
          >
            {revealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
        )}
        {value && (
          <button
            type="button"
            onClick={async () => { try { await navigator.clipboard.writeText(value); toast.success(`${label} copied`); } catch { /* ignore */ } }}
            className="px-2 rounded-md border border-slate-800 bg-slate-950 hover:bg-slate-800 text-slate-400 hover:text-slate-100"
            title={`Copy ${label}`}
            data-testid={`field-copy-${label.toLowerCase().replace(/\s+/g,"-")}`}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
